import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Camera, CheckCircle2, Loader2, RotateCcw, Square, Video } from "lucide-react";
import { toast } from "sonner";

/**
 * FmsPoseCapture — Markerless functional-movement
 * ───────────────────────────────────────────────────────────────────────
 * Records the athlete performing one Functional Movement Screen (FMS) pattern
 * (deep squat, hurdle step, inline lunge, ASLR, trunk-stability push-up,
 * rotary stability) and runs MediaPipe Pose Landmarker on every frame to
 * extract per-frame whole-body landmarks. The peak/midpoint biomechanics are
 * then evaluated against the published FMS rubric (ICC 0.92).
 *
 * Inference is on-device (MediaPipe Tasks Vision · WASM + GPU delegate);
 * raw video never leaves the browser.
 *
 * Per-movement heuristics implemented (auto-suggested score, coach overridable):
 *   • deep_squat       — hip-knee vertical drop, foot-flatness, dowel-overhead
 *                        retention (visible torso vertical)
 *   • hurdle_step      — peak hip flexion, weight-bearing knee stability,
 *                        trunk lean magnitude
 *   • inline_lunge     — back-knee floor contact, trunk vertical, balance loss
 *   • aslr             — moving-leg max hip flexion vs down-leg reference
 *   • trunk_pushup     — body rises as one unit (delta between shoulder-hip
 *                        and hip-knee planes)
 *   • rotary_stability — touch-elbow-to-knee under the body, contralateral
 *
 * Limitations: shoulder mobility and the active clearing tests still require
 * manual scoring (need a tape measure / palpation). Those screens skip the
 * camera step and go straight to manual grading.
 *
 * Props:
 *   movement     — one of the keys above
 *   recordSec    — duration of the capture clip (default 6s)
 *   onScore(0..3, evidence)   — auto-suggested score + per-frame extrema for review
 *   onCancel
 */

const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const WASM_BASE_URL =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

// MediaPipe pose-landmark indexes
const LM = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_HEEL: 29,     R_HEEL: 30,
};

function jointAngleDeg(a, b, c) {
  const v1x = a.x - b.x, v1y = a.y - b.y;
  const v2x = c.x - b.x, v2y = c.y - b.y;
  const dot = v1x * v2x + v1y * v2y;
  const m1 = Math.hypot(v1x, v1y);
  const m2 = Math.hypot(v2x, v2y);
  if (!m1 || !m2) return null;
  const cos = Math.max(-1, Math.min(1, dot / (m1 * m2)));
  return (Math.acos(cos) * 180) / Math.PI;
}

const mid = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

// ─── Per-movement scorers ─────────────────────────────────────────────────
// Each scorer receives the rolling history of pose snapshots (200ms ticks).
// Returns { score: 0..3, evidence: { ... key extrema ... } }.
function scoreDeepSquat(history) {
  if (!history.length) return { score: null, evidence: {} };
  // Hip-vs-knee Y at deepest descent: hips below knees → great range.
  let deepestRatio = -Infinity;          // (hipY - kneeY) — bigger = lower hip
  let torsoLeanMax = 0;
  for (const lm of history) {
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    if (!lh || !rh || !lk || !rk) continue;
    const hip = mid(lh, rh), knee = mid(lk, rk), sh = mid(ls, rs);
    deepestRatio = Math.max(deepestRatio, hip.y - knee.y);
    // Torso lean: angle of shoulder→hip vector from vertical
    const dy = hip.y - sh.y, dx = hip.x - sh.x;
    const leanDeg = Math.abs((Math.atan2(dx, dy) * 180) / Math.PI);
    torsoLeanMax = Math.max(torsoLeanMax, leanDeg);
  }
  // Score: hips clearly below knees (deepestRatio > 0.05 normalized image y)
  //        AND torso lean < 25° = 3
  //        partial depth OR moderate lean = 2
  //        cannot reach parallel = 1
  let score;
  if (deepestRatio > 0.05 && torsoLeanMax < 25) score = 3;
  else if (deepestRatio > 0.02) score = 2;
  else if (deepestRatio > -0.02) score = 1;
  else score = 1;
  return { score, evidence: { hip_below_knee: +deepestRatio.toFixed(3), max_torso_lean_deg: Math.round(torsoLeanMax) } };
}

function scoreHurdleStep(history) {
  if (!history.length) return { score: null, evidence: {} };
  // Peak hip flexion on moving leg (knee Y minus hip Y — knee rising above hip).
  let peakRise = -Infinity;
  let trunkLean = 0;
  for (const lm of history) {
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    if (!lh || !rh || !lk || !rk) continue;
    const hip = mid(lh, rh), sh = mid(ls, rs);
    // Pick whichever knee is higher (rising leg).
    const higherKneeY = Math.min(lk.y, rk.y);
    peakRise = Math.max(peakRise, hip.y - higherKneeY);  // positive = knee above hip
    const leanDeg = Math.abs(Math.atan2(hip.x - sh.x, hip.y - sh.y) * 180 / Math.PI);
    trunkLean = Math.max(trunkLean, leanDeg);
  }
  let score;
  if (peakRise > 0.02 && trunkLean < 20) score = 3;
  else if (peakRise > -0.02) score = 2;
  else score = 1;
  return { score, evidence: { peak_knee_above_hip: +peakRise.toFixed(3), max_trunk_lean_deg: Math.round(trunkLean) } };
}

function scoreInlineLunge(history) {
  if (!history.length) return { score: null, evidence: {} };
  // Back knee should approach the floor (ankle-vs-knee Y close); trunk stays vertical.
  let minBackKneeAnkleGap = Infinity;
  let trunkLean = 0;
  for (const lm of history) {
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    const la = lm[LM.L_ANKLE], ra = lm[LM.R_ANKLE];
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    if (!lk || !rk || !la || !ra) continue;
    const gap = Math.min(Math.abs(lk.y - la.y), Math.abs(rk.y - ra.y));
    minBackKneeAnkleGap = Math.min(minBackKneeAnkleGap, gap);
    const hip = mid(lh, rh), sh = mid(ls, rs);
    trunkLean = Math.max(trunkLean, Math.abs(Math.atan2(hip.x - sh.x, hip.y - sh.y) * 180 / Math.PI));
  }
  let score;
  if (minBackKneeAnkleGap < 0.08 && trunkLean < 15) score = 3;
  else if (minBackKneeAnkleGap < 0.15) score = 2;
  else score = 1;
  return { score, evidence: { back_knee_ankle_gap: +minBackKneeAnkleGap.toFixed(3), max_trunk_lean_deg: Math.round(trunkLean) } };
}

function scoreAslr(history) {
  if (!history.length) return { score: null, evidence: {} };
  // Highest ankle Y on either leg vs the kneecap Y of the OTHER leg.
  let bestRatio = -Infinity;
  for (const lm of history) {
    const la = lm[LM.L_ANKLE], ra = lm[LM.R_ANKLE];
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    if (!la || !ra || !lk || !rk) continue;
    // Compare each ankle (moving leg) with the opposite knee (down leg).
    bestRatio = Math.max(bestRatio, lk.y - ra.y, rk.y - la.y);
    // Also reference the down-leg mid-thigh as a fallback.
    bestRatio = Math.max(bestRatio, mid(lh, lk).y - ra.y, mid(rh, rk).y - la.y);
  }
  let score;
  if (bestRatio > 0.05) score = 3;       // ankle clears the knee
  else if (bestRatio > 0)  score = 2;    // ankle between mid-thigh and knee
  else if (bestRatio > -0.05) score = 1; // below mid-thigh line
  else score = 1;
  return { score, evidence: { ankle_vs_down_knee: +bestRatio.toFixed(3) } };
}

function scoreTrunkPushup(history) {
  if (!history.length) return { score: null, evidence: {} };
  // Rise as one unit → shoulder-hip-knee colinearity preserved through the lift.
  // We score by the MAX deviation of the hip from the shoulder→knee line.
  let maxDev = 0;
  for (const lm of history) {
    const ls = lm[LM.L_SHOULDER], rs = lm[LM.R_SHOULDER];
    const lh = lm[LM.L_HIP], rh = lm[LM.R_HIP];
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    if (!ls || !rs || !lh || !rh || !lk || !rk) continue;
    const s = mid(ls, rs), h = mid(lh, rh), k = mid(lk, rk);
    // perpendicular distance of h from line s→k, normalized to image space.
    const num = Math.abs((k.y - s.y) * h.x - (k.x - s.x) * h.y + k.x * s.y - k.y * s.x);
    const den = Math.hypot(k.y - s.y, k.x - s.x) || 1;
    maxDev = Math.max(maxDev, num / den);
  }
  let score;
  if (maxDev < 0.02) score = 3;
  else if (maxDev < 0.05) score = 2;
  else score = 1;
  return { score, evidence: { max_hip_sag: +maxDev.toFixed(3) } };
}

function scoreRotaryStability(history) {
  if (!history.length) return { score: null, evidence: {} };
  // Need elbow-to-knee close approach UNDER the body (contralateral pattern → 2, ipsilateral → 3).
  let minIpsi = Infinity, minContra = Infinity;
  for (const lm of history) {
    const le = lm[LM.L_ELBOW], re = lm[LM.R_ELBOW];
    const lk = lm[LM.L_KNEE], rk = lm[LM.R_KNEE];
    if (!le || !re || !lk || !rk) continue;
    minIpsi  = Math.min(minIpsi,  Math.hypot(le.x - lk.x, le.y - lk.y), Math.hypot(re.x - rk.x, re.y - rk.y));
    minContra = Math.min(minContra, Math.hypot(le.x - rk.x, le.y - rk.y), Math.hypot(re.x - lk.x, re.y - lk.y));
  }
  let score;
  if (minIpsi < 0.08)       score = 3;
  else if (minContra < 0.10) score = 2;
  else                      score = 1;
  return { score, evidence: { min_ipsilateral_dist: +minIpsi.toFixed(3), min_contralateral_dist: +minContra.toFixed(3) } };
}

const SCORERS = {
  deep_squat:             scoreDeepSquat,
  hurdle_step:            scoreHurdleStep,
  inline_lunge:           scoreInlineLunge,
  aslr:                   scoreAslr,
  trunk_stability_pushup: scoreTrunkPushup,
  rotary_stability:       scoreRotaryStability,
};

export default function FmsPoseCapture({ movement, recordSec = 6, onScore, onCancel }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const landmarkerRef = useRef(null);
  const streamRef = useRef(null);
  const captureRef = useRef([]);       // rolling history of landmark arrays
  const captureTimerRef = useRef(null);

  const [permission, setPermission] = useState("pending");
  const [phase, setPhase] = useState("setup");     // setup | loading | ready | recording | done
  const [secondsLeft, setSecondsLeft] = useState(recordSec);
  const [confidence, setConfidence] = useState(0);
  const [autoResult, setAutoResult] = useState(null);
  const [error, setError] = useState(null);

  const scorer = useMemo(() => SCORERS[movement] || null, [movement]);

  // ─── MediaPipe init ──────────────────────────────────────────────────
  const initModel = async () => {
    setPhase("loading");
    try {
      const vision = await import("@mediapipe/tasks-vision");
      const fileset = await vision.FilesetResolver.forVisionTasks(WASM_BASE_URL);
      landmarkerRef.current = await vision.PoseLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO",
        numPoses: 1,
        minPoseDetectionConfidence: 0.5,
        minPosePresenceConfidence: 0.5,
        minTrackingConfidence: 0.5,
      });
      setPhase("ready");
    } catch (e) {
      console.error("MediaPipe init failed", e);
      setError("Pose detector could not load — switch to manual grading.");
      setPermission("unsupported");
      setPhase("setup");
    }
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setPermission("unsupported");
      setError("This browser doesn't expose a camera API.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment", width: { ideal: 720 }, height: { ideal: 1280 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setPermission("granted");
      if (!landmarkerRef.current) await initModel();
    } catch (e) {
      console.error(e);
      setPermission("denied");
      setError("Camera permission denied.");
    }
  };

  // ─── Per-frame inference + draw ─────────────────────────────────────
  useEffect(() => {
    if (phase !== "ready" && phase !== "recording") return;
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || !landmarkerRef.current) return;
    const ctx = c.getContext("2d");
    const loop = () => {
      if (!videoRef.current || !landmarkerRef.current) return;
      try {
        const res = landmarkerRef.current.detectForVideo(v, performance.now());
        c.width = v.videoWidth; c.height = v.videoHeight;
        ctx.drawImage(v, 0, 0, c.width, c.height);
        const pose = res?.landmarks?.[0];
        if (pose) {
          const conf = Math.min(...[LM.L_HIP, LM.R_HIP, LM.L_KNEE, LM.R_KNEE].map(i => pose[i]?.visibility ?? 1));
          setConfidence(conf);
          // Draw skeleton
          const px = (lm) => ({ x: lm.x * c.width, y: lm.y * c.height });
          const seg = [
            [LM.L_SHOULDER, LM.R_SHOULDER], [LM.L_SHOULDER, LM.L_HIP], [LM.R_SHOULDER, LM.R_HIP],
            [LM.L_HIP, LM.R_HIP], [LM.L_HIP, LM.L_KNEE], [LM.R_HIP, LM.R_KNEE],
            [LM.L_KNEE, LM.L_ANKLE], [LM.R_KNEE, LM.R_ANKLE],
            [LM.L_SHOULDER, LM.L_ELBOW], [LM.L_ELBOW, LM.L_WRIST],
            [LM.R_SHOULDER, LM.R_ELBOW], [LM.R_ELBOW, LM.R_WRIST],
          ];
          ctx.lineWidth = 5; ctx.lineCap = "round"; ctx.strokeStyle = "rgba(34,211,238,0.9)";
          for (const [a, b] of seg) {
            const pa = px(pose[a]), pb = px(pose[b]);
            ctx.beginPath(); ctx.moveTo(pa.x, pa.y); ctx.lineTo(pb.x, pb.y); ctx.stroke();
          }
          for (const i of [LM.L_HIP, LM.R_HIP, LM.L_KNEE, LM.R_KNEE, LM.L_SHOULDER, LM.R_SHOULDER]) {
            const p = px(pose[i]);
            ctx.fillStyle = "rgba(217,4,41,0.95)";
            ctx.beginPath(); ctx.arc(p.x, p.y, 6, 0, Math.PI * 2); ctx.fill();
          }
          if (phase === "recording") {
            captureRef.current.push(pose);
          }
        } else {
          setConfidence(0);
        }
      } catch (e) { /* noop per-frame */ }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [phase]);

  useEffect(() => () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (captureTimerRef.current) clearInterval(captureTimerRef.current);
    streamRef.current?.getTracks().forEach((t) => t.stop());
  }, []);

  // ─── Start recording ─────────────────────────────────────────────────
  const startRecording = () => {
    captureRef.current = [];
    setSecondsLeft(recordSec);
    setPhase("recording");
    captureTimerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(captureTimerRef.current);
          finishRecording();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const finishRecording = () => {
    setPhase("done");
    if (scorer) {
      const result = scorer(captureRef.current);
      setAutoResult(result);
    } else {
      // Movements without a heuristic scorer fall through to manual entry.
      setAutoResult({ score: null, evidence: {} });
    }
  };

  const stopEarly = () => {
    if (captureTimerRef.current) clearInterval(captureTimerRef.current);
    finishRecording();
  };

  const reset = () => {
    captureRef.current = [];
    setAutoResult(null);
    setPhase("ready");
  };

  // ─── Render ──────────────────────────────────────────────────────────
  return (
    <Card className="bg-[var(--ink-2)] border-[var(--ink-border)] p-6" data-testid={`fms-pose-${movement}`}>
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[var(--brand-secondary)]">Markerless capture</div>
          <div className="text-lg font-display font-black text-white mt-1">Record your {movement.replace(/_/g, " ")}</div>
        </div>
        <Video className="h-5 w-5 text-white/40" />
      </div>

      <div className="relative rounded-xl overflow-hidden bg-black border border-white/10 mb-3"
           style={{ aspectRatio: "9 / 16", maxHeight: 480 }}>
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover opacity-0" muted playsInline />
        <canvas ref={canvasRef} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute top-3 left-3 right-3 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] font-bold text-white">
          <span className="bg-black/60 backdrop-blur-md px-2.5 py-1.5 rounded">
            {phase === "setup" && "Camera not started"}
            {phase === "loading" && "Loading pose detector…"}
            {phase === "ready" && "Position yourself in frame"}
            {phase === "recording" && `Recording · ${secondsLeft}s`}
            {phase === "done" && "Capture complete"}
          </span>
          {(phase === "ready" || phase === "recording") && (
            <span className="bg-black/60 backdrop-blur-md px-2.5 py-1.5 rounded">Conf {(confidence * 100).toFixed(0)}%</span>
          )}
        </div>
        {phase === "loading" && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading on-device pose model…
          </div>
        )}
      </div>

      {/* Controls */}
      {permission === "pending" && phase === "setup" && (
        <Button onClick={startCamera} className="w-full" data-testid="fms-grant-camera">
          <Camera className="h-4 w-4 mr-2" /> Enable Camera
        </Button>
      )}

      {phase === "ready" && (
        <Button onClick={startRecording} className="w-full" style={{ backgroundColor: "var(--brand-secondary)" }}
                data-testid="fms-start-record">
          ▶ Record {recordSec}s clip
        </Button>
      )}

      {phase === "recording" && (
        <Button onClick={stopEarly} variant="destructive" className="w-full" data-testid="fms-stop-record">
          <Square className="h-4 w-4 mr-2" /> Stop early ({secondsLeft}s left)
        </Button>
      )}

      {phase === "done" && (
        <div className="space-y-3">
          {autoResult?.score !== null && autoResult?.score !== undefined && (
            <div className="rounded-lg p-4 border border-emerald-500/30 bg-emerald-500/5">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 mb-1">Auto-suggested score</div>
              <div className="text-3xl font-display font-black text-white">{autoResult.score} / 3</div>
              <div className="text-[10px] text-white/50 mt-1">Based on biomechanical extrema across the clip. Coach can override.</div>
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={reset} variant="outline" className="flex-1 border-white/20 text-white" data-testid="fms-retake">
              <RotateCcw className="h-4 w-4 mr-2" /> Re-record
            </Button>
            <Button onClick={() => onScore?.(autoResult?.score, autoResult?.evidence)}
                    className="flex-1" style={{ backgroundColor: "var(--brand-secondary)" }} data-testid="fms-use-auto-score">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Use score
            </Button>
          </div>
        </div>
      )}

      {(permission === "denied" || permission === "unsupported") && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mt-2 text-xs text-amber-200">
          {error || "Camera unavailable."} Skip the recording — score manually below.
        </div>
      )}

      <button onClick={onCancel} className="mt-4 text-xs text-white/40 hover:text-white/70 w-full" data-testid="fms-pose-cancel">
        Skip the recording — score this movement manually
      </button>

      <div className="mt-3 text-[10px] text-white/30 text-center">
        On-device inference · No video leaves your phone · Powered by MediaPipe pose
      </div>
    </Card>
  );
}
